<template>
  <!-- The campaigns list's Bid cell for a RingbaRTB or Programmatic campaign with a number to show
       (R65; owner, 2026-11-01): the bid, with an icon for where it came from (returned on its pings
       or auctions, or set in its settings), and under it the minimum, or, for a campaign that varies
       its minimum, a bar of the share at each one with the numbers on hover (I23). Read-only, so it
       never passes for the Static input beside it. -->
  <div
    v-b-tooltip.hover
    class="bid-terms"
    :title="tooltip"
    data-cy="text-bid-terms"
  >
    <div class="bid-terms__bid">
      {{ formatCurrency(terms.bidInUSD) }}
      <font-awesome-icon
        :icon="['fas', isConfigured ? 'cog' : 'exchange-alt']"
        class="bid-terms__source ml-1 text-muted"
        :data-cy="`icon-bid-terms-${isConfigured ? 'settings' : 'returned'}`"
      />
    </div>

    <div
      v-if="split.length"
      class="bid-terms__split text-muted"
      data-cy="bid-terms-split"
    >
      <span>{{ split[0].minimumSeconds }} s</span>
      <span
        class="bid-terms__bar"
        role="img"
        :aria-label="tooltip"
      >
        <span
          v-for="(entry, index) in split"
          :key="entry.minimumSeconds"
          class="bid-terms__segment"
          :style="segmentStyle(entry, index)"
        />
      </span>
      <span>{{ split[split.length - 1].minimumSeconds }} s</span>
    </div>

    <div
      v-else-if="minimumText"
      class="bid-terms__minimum text-muted"
      data-cy="text-bid-terms-minimum"
    >
      {{ minimumText }}
    </div>
  </div>
</template>

<script>
import formatCurrency from '@/utils/formatCurrency'
import { getBidTermsMinimumText, getBidTermsTooltip } from '@/store/modules/market-context.store'

// The share bar's segments fade from the shortest minimum to the longest, so neighbours stay apart
// with any number of them. The lightest stays as strong as the Market Position band beside it, so it
// never reads as empty track
const SEGMENT_OPACITY_MAX = 0.9
const SEGMENT_OPACITY_MIN = 0.45

export default {
  name: 'DynamicBidTerms',

  props: {
    // What the campaign bids now, from getBidColumnTerms in the store
    terms: {
      type: Object,
      required: true
    },

    // A CPA-only category has no minimum, so the cell shows the bid alone
    isCpaOnly: {
      type: Boolean,
      default: false
    },

    // The list's window as the `long` form of formatMarketWindow, for the hover. Empty until the
    // positions load, and the phrase is left out then
    windowLong: {
      type: String,
      default: ''
    }
  },

  computed: {
    isConfigured() {
      return this.terms.source === 'Configuration'
    },

    // The minimums a varying campaign returns, shortest first, so the bar's end labels are its range
    split() {
      if (this.isCpaOnly) {
        return []
      }

      return [...(this.terms.byMinimum || [])].sort((a, b) => a.minimumSeconds - b.minimumSeconds)
    },

    minimumText() {
      return getBidTermsMinimumText(this.terms, { isCpaOnly: this.isCpaOnly })
    },

    tooltip() {
      return getBidTermsTooltip(this.terms, { long: this.windowLong }, { isCpaOnly: this.isCpaOnly })
    }
  },

  methods: {
    formatCurrency,

    segmentStyle(entry, index) {
      const steps = Math.max(1, this.split.length - 1)
      const opacity = SEGMENT_OPACITY_MAX - ((SEGMENT_OPACITY_MAX - SEGMENT_OPACITY_MIN) * index) / steps

      return { width: `${entry.sharePercent}%`, opacity }
    }
  }
}
</script>

<style lang="scss" scoped>
// Each line follows the cell's own alignment: right in the table's column, left in TouchTable's
// cards on a phone
.bid-terms {
  display: inline-block;
  white-space: nowrap;
}

.bid-terms__source {
  font-size: 0.75rem;
}

.bid-terms__minimum,
.bid-terms__split {
  font-size: 0.75rem;
}

.bid-terms__split {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 3px;
}

// As tall and as round as the Market Position bar beside it. The gap between segments shows the
// track, so two minimums read as two parts rather than one bar filled part way
.bid-terms__bar {
  display: flex;
  gap: 2px;
  width: 56px;
  height: 6px;
  overflow: hidden;
  border-radius: 3px;
  background-color: $violet-dark;
}

.bid-terms__segment {
  height: 100%;
  background-color: $blue;
}
</style>
